import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Database, Edit, Link, Plus, Trash, Unlink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ScadaConnectorForm from '@/components/settings/ScadaConnectorForm';
import { useToast } from '@/hooks/use-toast';
import { ScadaConnector } from './types';

interface ScadaConnectorSettingsProps {
  editing: boolean;
}

const ScadaConnectorSettings: React.FC<ScadaConnectorSettingsProps> = ({ editing }) => {
  const [scadaConnectors, setScadaConnectors] = React.useState<ScadaConnector[]>([
    { id: '1', name: 'Plant SCADA System', type: 'OPC UA', host: '192.168.1.100', port: '4840', protocol: 'TCP/IP', status: 'active' },
    { id: '2', name: 'Energy Monitoring', type: 'Modbus', host: '192.168.1.101', port: '502', protocol: 'TCP', status: 'active' },
    { id: '3', name: 'Water Treatment', type: 'DNP3', host: '192.168.1.102', port: '20000', protocol: 'TCP/IP', status: 'inactive' }
  ]);
  const [isAddingConnector, setIsAddingConnector] = React.useState(false);
  const [editingConnector, setEditingConnector] = React.useState<ScadaConnector | null>(null);
  const { toast } = useToast();

  const handleAddConnector = (connector: Omit<ScadaConnector, 'id'>) => {
    const newConnector = {
      ...connector,
      id: Date.now().toString(),
    };
    setScadaConnectors([...scadaConnectors, newConnector as ScadaConnector]);
    setIsAddingConnector(false);
    toast({
      title: "Connector Added",
      description: `${connector.name} has been added successfully.`
    });
  };

  const handleUpdateConnector = (updatedConnector: ScadaConnector) => {
    setScadaConnectors(scadaConnectors.map(c => 
      c.id === updatedConnector.id ? updatedConnector : c
    ));
    setEditingConnector(null);
    toast({
      title: "Connector Updated",
      description: `${updatedConnector.name} has been updated successfully.`
    });
  };

  const handleDeleteConnector = (id: string) => {
    const connectorToDelete = scadaConnectors.find(c => c.id === id);
    if (connectorToDelete) {
      setScadaConnectors(scadaConnectors.filter(c => c.id !== id));
      toast({
        title: "Connector Removed",
        description: `${connectorToDelete.name} has been removed successfully.`
      });
    }
  };

  const toggleConnectorStatus = (id: string) => {
    setScadaConnectors(scadaConnectors.map(c => 
      c.id === id ? { ...c, status: c.status === 'active' ? 'inactive' : 'active' } : c
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">SCADA Data Connectors</h3>
        {editing && (
          <Button 
            onClick={() => setIsAddingConnector(true)} 
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Add Connector
          </Button>
        )}
      </div>
      
      <Alert variant="default" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <Database className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertTitle className="text-blue-700 dark:text-blue-400">Data Integration</AlertTitle>
        <AlertDescription className="text-blue-600 dark:text-blue-400">
          Configure and manage connections to external SCADA and control systems
        </AlertDescription>
      </Alert>
      
      {isAddingConnector && (
        <Card className="border-dashed border-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Add New SCADA Connector</CardTitle>
          </CardHeader>
          <CardContent>
            <ScadaConnectorForm 
              onSubmit={handleAddConnector} 
              onCancel={() => setIsAddingConnector(false)}
            />
          </CardContent>
        </Card>
      )}
      
      {editingConnector && (
        <Card className="border-blue-200 dark:border-blue-800 border-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Edit SCADA Connector</CardTitle>
          </CardHeader>
          <CardContent>
            <ScadaConnectorForm 
              initialValues={editingConnector}
              onSubmit={handleUpdateConnector} 
              onCancel={() => setEditingConnector(null)}
              isEditing
            />
          </CardContent>
        </Card>
      )}
      
      <div className="border rounded-md">
        <ScrollArea className="max-h-[400px]">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="h-10 px-4 text-left font-medium">Name</th>
                <th className="h-10 px-4 text-left font-medium">Type</th>
                <th className="h-10 px-4 text-left font-medium">Host:Port</th>
                <th className="h-10 px-4 text-left font-medium">Protocol</th>
                <th className="h-10 px-4 text-left font-medium">Status</th>
                <th className="h-10 px-4 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scadaConnectors.map((connector) => (
                <tr key={connector.id} className="border-b">
                  <td className="p-4 align-middle">{connector.name}</td>
                  <td className="p-4 align-middle">{connector.type}</td>
                  <td className="p-4 align-middle font-mono text-sm">{connector.host}:{connector.port}</td>
                  <td className="p-4 align-middle">{connector.protocol}</td>
                  <td className="p-4 align-middle">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      connector.status === 'active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
                    }`}>
                      {connector.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-4 align-middle text-right">
                    <div className="flex justify-end space-x-1">
                      {editing ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => toggleConnectorStatus(connector.id)}
                            title={connector.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {connector.status === 'active' ? <Unlink className="h-4 w-4" /> : <Link className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setEditingConnector(connector)}
                            title="Edit connector"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                                title="Delete connector"
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72">
                              <div className="space-y-3">
                                <h4 className="font-medium">Confirm deletion</h4>
                                <p className="text-sm text-muted-foreground">
                                  Are you sure you want to delete the connector "{connector.name}"? This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                  >
                                    Cancel
                                  </Button>
                                  <Button 
                                    variant="destructive" 
                                    size="sm"
                                    onClick={() => handleDeleteConnector(connector.id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            toast({
                              title: "View only mode",
                              description: "Edit mode is required to manage SCADA connectors."
                            });
                          }}
                        >
                          Details
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </div>
      
      <div className="space-y-2 pt-2">
        <h4 className="text-sm font-medium">Connection Refresh Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="connection-retry">Connection Retry Attempts</Label>
            <Input 
              id="connection-retry" 
              type="number" 
              placeholder="3" 
              defaultValue="3" 
              readOnly={!editing} 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="connection-timeout">Connection Timeout (seconds)</Label>
            <Input 
              id="connection-timeout" 
              type="number" 
              placeholder="30" 
              defaultValue="30" 
              readOnly={!editing} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScadaConnectorSettings;
